import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { IconButton, IconButtonProps, Tooltip } from "@mui/material";
import React, { FC, PropsWithChildren, useCallback } from "react";
import { Wrapper } from "./styles";
import { useCopyToClipboard, CopyToClipboardOptions } from "./useCopyToClipboard";

export interface CopyToClipboardButtonProps extends CopyToClipboardOptions {
  /** The text to copy to clipboard */
  text: string;
  /** Button variant - 'button' shows children with copy icon, 'icon' shows only the icon */
  variant?: 'button' | 'icon';
  /** Props to pass to the underlying IconButton */
  iconButtonProps?: Omit<IconButtonProps, 'onClick' | 'disabled'>;
  /** Tooltip text for the copy button */
  tooltip?: string;
  /** Whether to show tooltip */
  showTooltip?: boolean;
  /** Animation duration in milliseconds */
  animationDuration?: number;
  /** Size of the icon button */
  size?: IconButtonProps['size'];
  /** Color of the icon */
  color?: IconButtonProps['color'];
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Custom aria-label for accessibility */
  ariaLabel?: string;
}

export const CopyToClipboardButton: FC<PropsWithChildren<CopyToClipboardButtonProps>> = ({
  text,
  children,
  variant = children ? 'button' : 'icon',
  iconButtonProps = {},
  tooltip,
  showTooltip = true,
  animationDuration = 400,
  size = 'small',
  color = 'default',
  disabled = false,
  ariaLabel,
  ...copyOptions
}) => {
  // Don't render anything if there's no text to copy
  if (!text || text.trim() === '') {
    return null;
  }

  const { copyToClipboard, isCopying, isRecentlyCopied } = useCopyToClipboard(copyOptions);

  const handleCopy = useCallback(async (event: React.MouseEvent) => {
    // Prevent event bubbling and default behavior
    event.preventDefault();
    event.stopPropagation();

    if (!text || disabled) return;

    await copyToClipboard(text);
  }, [text, disabled, copyToClipboard]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Create a synthetic mouse event for the copy handler
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.MouseEvent;
      handleCopy(syntheticEvent);
    }
  }, [handleCopy]);

  const getIconColor = (): 'inherit' | 'action' | 'disabled' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (disabled) return 'disabled';
    if (isRecentlyCopied) return 'primary';
    if (variant === 'button') return 'action';
    
    // Map IconButton colors to SvgIcon colors
    switch (color) {
      case 'default':
        return 'action';
      case 'inherit':
      case 'primary':
      case 'secondary':
      case 'error':
      case 'info':
      case 'success':
      case 'warning':
        return color;
      default:
        return 'action';
    }
  };

  const getTooltipText = () => {
    if (tooltip) return tooltip;
    if (isCopying) return 'Copying...';
    if (isRecentlyCopied) return 'Copied!';
    return 'Copy to clipboard';
  };

  const iconButton = (
    <IconButton
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      size={size}
      disabled={disabled || isCopying}
      aria-label={ariaLabel || `Copy ${text} to clipboard`}
      role="button"
      tabIndex={0}
      {...iconButtonProps}
    >
      <ContentCopyIcon 
        color={getIconColor()}
        fontSize="inherit" 
      />
    </IconButton>
  );

  const buttonContent = showTooltip ? (
    <Tooltip title={getTooltipText()} arrow>
      {iconButton}
    </Tooltip>
  ) : iconButton;

  // Always wrap in styled container for animation support
  return (
    <Wrapper 
      $variant={variant}
      $animationDuration={animationDuration}
      $isRecentlyCopied={isRecentlyCopied}
    >
      {variant === 'button' && children}
      {buttonContent}
    </Wrapper>
  );
};
